import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Stabilni prefixi cache entrija. Hardkodirane stringove ne koristiti —
 * svaki dropdown/invalidacija mora koristiti enum da prefixi ostanu konzistentni.
 */
export enum DropdownCacheKey {
  Contacts = "contacts",
  ContactGroups = "contactGroups",
  FleetVehicles = "fleetVehicles",
  FleetWarehouses = "fleetWarehouses",
  VehicleGroups = "vehicleGroups",
  VehicleManufacturers = "vehicleManufacturers",
  VehicleModels = "vehicleModels",
  VehicleTypes = "vehicleTypes",
  TireManufacturers = "tireManufacturers",
  TireTypes = "tireTypes",
  Drivers = "drivers",
  VehicleDamages = "vehicleDamages",
  InvoiceStatuses = "invoiceStatuses",
  OfferSaleStatuses = "offerSaleStatuses",
  OfferRentStatuses = "offerRentStatuses",
  ContractSaleStatuses = "contractSaleStatuses",
  ContractRentStatuses = "contractRentStatuses",
  FleetOfferSales = "fleetOfferSales",
  FleetOfferRents = "fleetOfferRents",
  FleetContractSales = "fleetContractSales",
  FleetContractRents = "fleetContractRents",
  Offices = "offices",
  Users = "users",
  BillingItemTypes = "billingItemTypes",
  UnitsOfMeasure = "unitsOfMeasure",
  ExpenseTypes = "expenseTypes",
}

/**
 * Slaže cache key iz enum prefixa i proizvoljnih parametara.
 * Primjeri:
 *   buildDropdownCacheKey(DropdownCacheKey.FleetVehicles, searchValue)
 *   buildDropdownCacheKey(DropdownCacheKey.Contacts, contactTypeId, groupIds?.join(","), searchValue)
 */
export const buildDropdownCacheKey = (
  key: DropdownCacheKey,
  ...params: Array<string | number | boolean | null | undefined>
): string => {
  if (params.length === 0) return key;
  return [key, ...params.map((p) => (p === null || p === undefined ? "" : String(p)))].join(":");
};

interface CacheEntry<T> {
  data?: T;
  timestamp: number;
  promise?: Promise<T>;
}

const cache = new Map<string, CacheEntry<any>>();

/**
 * Brisanje zapisa ne bi samo po sebi ništa osvježilo — montirani dropdown je podatke već
 * pročitao. Zato invalidacija javi verziju svima, pa oni ponovno prođu kroz `load`;
 * ključevi koji nisu obrisani i dalje se čitaju iz keša, bez novog zahtjeva.
 */
let cacheVersion = 0;
const versionListeners = new Set<(version: number) => void>();

/**
 * Module-level cache hook za dropdown podatke.
 * - Module scope: preživljava unmount komponenti, dijeli se između instanci.
 * - TTL: nakon isteka entry se smatra nevažećim i ponovno se fetcha.
 * - Deduplikacija: ako N instanci montira se istovremeno, samo 1 fetch — ostale čekaju isti promise.
 */
export function useDropdownCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = 5 * 60 * 1000,
  enabled: boolean = true
): { data: T | null; isLoading: boolean; refresh: () => void } {
  const [data, setData] = useState<T | null>(() => {
    if (!enabled) return null;
    const entry = cache.get(key);
    if (entry?.data !== undefined && Date.now() - entry.timestamp < ttlMs) {
      return entry.data;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [version, setVersion] = useState<number>(cacheVersion);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    versionListeners.add(setVersion);
    // Lista je možda invalidirana dok ovaj dropdown nije bio montiran.
    setVersion(cacheVersion);

    return () => {
      versionListeners.delete(setVersion);
    };
  }, []);

  const load = useCallback(async () => {
    // Preskoči fetch kad komponenta eksplicitno signalizira da joj trebaju required parametri (npr. dependency dropdown bez parent ID-a).
    if (!enabled) return;

    const entry = cache.get(key);
    const fresh = entry?.data !== undefined && Date.now() - entry.timestamp < ttlMs;

    if (fresh) {
      setData(entry!.data as T);
      return;
    }

    // Ako je već u tijeku fetch za isti key — pridruži se postojećem promise-u
    if (entry?.promise) {
      setIsLoading(true);
      try {
        const result = await entry.promise;
        if (mountedRef.current) setData(result);
      } catch {
        if (mountedRef.current) setData(null);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
      return;
    }

    // Prvi fetch — rezerviramo promise u cache-u za deduplikaciju
    setIsLoading(true);
    const promise = fetcher();
    cache.set(key, { timestamp: Date.now(), promise });

    try {
      const result = await promise;
      cache.set(key, { data: result, timestamp: Date.now() });
      if (mountedRef.current) setData(result);
    } catch (err) {
      cache.delete(key);
      if (mountedRef.current) setData(null);
      throw err;
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
    // `version` je namjerno u ovisnostima: nakon invalidacije tjera ponovni prolaz,
    // pa se obrisani ključ dohvati iznova.
  }, [key, ttlMs, enabled, version]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const refresh = useCallback(() => {
    cache.delete(key);
    load().catch(() => {});
  }, [key, load]);

  return { data, isLoading, refresh };
}

/**
 * Invalidira cache entrije za zadani key (briše i exact match i sve prefix-matchane dynamic entrije).
 * Bez argumenta briše sve.
 * Primjer: invalidateDropdownCache(DropdownCacheKey.FleetVehicles) nakon kreiranja novog vozila.
 */
export const invalidateDropdownCache = (key?: DropdownCacheKey) => {
  if (!key) {
    cache.clear();
  } else {
    const prefix = `${key}:`;
    for (const k of Array.from(cache.keys())) {
      if (k === key || k.startsWith(prefix)) {
        cache.delete(k);
      }
    }
  }

  // Montirani dropdowni sami ne bi primijetili brisanje — ovime ponovno dohvaćaju listu.
  cacheVersion += 1;
  versionListeners.forEach((listener) => listener(cacheVersion));
};
